"use client";

import {
  Activity,
  AudioLines,
  Bell,
  CalendarDays,
  Check,
  ChevronLeft,
  ClipboardCheck,
  Clock3,
  Eye,
  EyeOff,
  FileClock,
  FilePlus2,
  HelpCircle,
  LayoutDashboard,
  LoaderCircle,
  LockKeyhole,
  Mic,
  Pause,
  Play,
  Plus,
  Search,
  ShieldCheck,
  Sparkles,
  Square,
  Stethoscope,
  Trash2,
  UsersRound,
  WandSparkles,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { wardPatients, type WardPatient } from "@/src/mocks/patient";
import { processSoapDictation, structureEncounter } from "@/src/services/aiAgentService";
import { AudioRecorder, transcribeWithCariva } from "@/src/services/carivaAsr";
import { parseOrdersFromPlan } from "@/src/services/planOrders";
import { AccountButton } from "./AuthPanel";
import { useLang } from "@/src/i18n";
import { localizedName } from "@/src/i18n";
import {
  ghostSuggestions,
  loadCustomTemplates,
  loadTemplate,
  saveCustomTemplate,
  type AiAuditContext,
  type GhostSuggestion,
  type OrderCategory,
  type OrderLane,
  type ParsedOrder,
  type SoapSection,
  type TemplatePreview,
} from "@/src/services/mockAiService";
import {
  confirm as confirmClinical,
  ensureEncounter,
  saveNote as saveNoteApi,
  saveOrders as saveOrdersApi,
} from "@/src/services/clinicalApi";
import { AuditDrawer, type AuditEntry } from "./AuditDrawer";
import { AuditorPanel } from "./AuditorPanel";
import { ClinicalViewer } from "./ClinicalViewer";
import { HeartbeatFeed } from "./HeartbeatFeed";
import { HelpDeskPanel } from "./HelpDeskPanel";
import { OrderChip } from "./OrderChip";
import { OrderOmnibox } from "./OrderOmnibox";
import { LaneOrderSearch } from "./LaneOrderSearch";
import { PatientBanner } from "./PatientBanner";
import { SoapCard } from "./SoapCard";
import { TemplateBuilderModal } from "./TemplateBuilderModal";
import { TemplateModal } from "./TemplateModal";

type SoapState = Record<SoapSection, string>;
type DictationState = "idle" | "recording" | "paused" | "processing";
type DictationTarget = SoapSection | "all";
type Tab = "dashboard" | "patients" | "rounds";

const initialSoap: SoapState = {
  subjective: "",
  objective: "",
  assessment: "Anemia (D64.9), symptomatic; etiology under evaluation.",
  plan: "Review transfusion threshold and consent with the patient.",
};

const initialAudit: AuditEntry[] = [
  {
    id: "v3",
    at: "23 Jul 2026 · 16:40",
    user: "Dr. Kittisak",
    action: "Progress note updated",
    diff: "+ Repeat CBC scheduled for 08:00; anemia evaluation continued.",
    takeOrder: false,
    provenance: "User-entered",
  },
  {
    id: "v2",
    at: "23 Jul 2026 · 08:10",
    user: "Dr. Heartbeat",
    action: "AI suggestion accepted",
    diff: "+ No melena or hematemesis reported.",
    takeOrder: false,
    provenance: "AI-suggested → accepted",
    userInput: "Fatigue noted.",
    aiSuggestion: "No melena or hematemesis reported.",
    selectedOutput: "Fatigue noted. No melena or hematemesis reported.",
    evidence: "CBC 08:00 · Anemia evidence schema",
    model: "Nong SATI Heartbeat v2",
  },
  {
    id: "v1",
    at: "22 Jul 2026 · 14:26",
    user: "Dr. Kittisak",
    action: "Admission note created",
    diff: "Initial SOAP progression opened for this encounter.",
    takeOrder: false,
    provenance: "User-entered",
  },
];

function nowLabel() {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date());
}

function TopNav({
  activeTab,
  onNavigate,
  onHome,
  search,
  onSearch,
  results,
  onSelectResult,
}: {
  activeTab: Tab;
  onNavigate: (tab: Tab) => void;
  onHome: () => void;
  search: string;
  onSearch: (value: string) => void;
  results: WardPatient[];
  onSelectResult: (patient: WardPatient) => void;
}) {
  const { t, lang, setLang } = useLang();
  return (
    <nav className="top-nav">
      <button type="button" className="brand" onClick={onHome} title={t("nav.back")} aria-label={`${t("app.name")} — ${t("nav.back")}`}>
        <div className="brand-logo">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/sati-logo.png" alt="Sati" width="34" height="34" />
        </div>
        <span>{t("app.name")}</span>
      </button>
      <div className="nav-links">
        <button type="button" className={activeTab === "dashboard" ? "active" : ""} onClick={() => onNavigate("dashboard")}><LayoutDashboard size={15} /> {t("nav.dashboard")}</button>
        <button type="button" className={activeTab === "patients" ? "active" : ""} onClick={() => onNavigate("patients")}><UsersRound size={15} /> {t("nav.patients")}</button>
        <button type="button" className={activeTab === "rounds" ? "active" : ""} onClick={() => onNavigate("rounds")}><Stethoscope size={15} /> {t("nav.rounds")}</button>
      </div>
      <div className="global-search-wrap">
        <div className="global-search">
          <Search size={16} />
          <input
            aria-label={t("nav.search.aria")}
            placeholder={t("nav.search.placeholder")}
            value={search}
            onChange={(event) => onSearch(event.target.value)}
          />
          <kbd>⌘K</kbd>
        </div>
        {search.trim() && (
          <div className="search-results" role="listbox" aria-label="Search results">
            {results.length ? results.map((item) => (
              <button type="button" key={item.id} className="search-result" onClick={() => onSelectResult(item)}>
                <span className="search-result-avatar">{localizedName(lang, item.name, item.englishName).slice(0, 1)}</span>
                <span className="search-result-copy">
                  <strong>{localizedName(lang, item.name, item.englishName)}</strong>
                  <small>HN {item.hn} · {item.ward} {item.bed} · {item.diagnosis}</small>
                </span>
                <span className={`patient-status ${item.status.toLowerCase()}`}>{item.status}</span>
              </button>
            )) : (
              <div className="search-empty">{t("search.empty")} “{search}”.</div>
            )}
          </div>
        )}
      </div>
      <div className="nav-lang-toggle" role="group" aria-label={t("lang.toggle.aria")}>
        <button type="button" className={lang === "th" ? "on" : ""} aria-pressed={lang === "th"} onClick={() => setLang("th")}>ไทย</button>
        <button type="button" className={lang === "en" ? "on" : ""} aria-pressed={lang === "en"} onClick={() => setLang("en")}>EN</button>
      </div>
      <button className="nav-bell" type="button" aria-label={t("nav.notifications")}><Bell size={17} /><span /></button>
      <div className="user-chip">
        <div className="user-avatar">DH</div>
        <div><strong>Dr. Heartbeat</strong><span>{t("user.role.cardiologist")}</span></div>
        <AccountButton />
      </div>
    </nav>
  );
}

function WardList({ onOpen }: { onOpen: (patient: WardPatient) => void }) {
  const { t, lang } = useLang();
  const completed = 2;
  return (
    <main className="ward-page">
      <div className="ward-hero">
        <div>
          <span className="eyebrow"><Activity size={14} /> {t("ward.service")}</span>
          <h1>{t("ward.todayRound")}</h1>
          <p>{t("ward.todayRound.sub")}</p>
        </div>
        <div className="round-status">
          <span>{t("ward.roundCompleteness")}</span>
          <strong>68%</strong>
          <div><i /></div>
          <small>{completed} / {wardPatients.length} {t("ward.notesInProgress")}</small>
        </div>
      </div>
      <section className="ward-card">
        <div className="ward-card-head">
          <div><h2>{t("ward.list.title")}</h2><p>{t("ward.list.sub")}</p></div>
          <div><span className="secure-label"><LockKeyhole size={13} /> {t("ward.sso")}</span><button type="button"><Plus size={14} /> {t("ward.addPatient")}</button></div>
        </div>
        <div className="ward-table-wrap">
          <table className="ward-table">
            <thead>
              <tr><th>{t("ward.col.patient")}</th><th>{t("ward.col.location")}</th><th>{t("ward.col.problem")}</th><th>{t("ward.col.status")}</th><th>{t("ward.col.activity")}</th><th /></tr>
            </thead>
            <tbody>
              {wardPatients.map((item) => (
                <tr key={item.id} className={item.id === "pt-athena" ? "featured-row" : ""}>
                  <td>
                    <button type="button" className="patient-cell" onClick={() => onOpen(item)}>
                      <div><strong>{localizedName(lang, item.name, item.englishName)}</strong><small>HN {item.hn} · AN {item.an} · {item.age}Y</small></div>
                    </button>
                  </td>
                  <td><strong>{item.ward}</strong><small>{t("ward.bed")} {item.bed}</small></td>
                  <td>{item.diagnosis}</td>
                  <td><span className={`patient-status ${item.status.toLowerCase()}`}>{t(`status.${item.status.toLowerCase()}`)}</span></td>
                  <td>{item.updated}</td>
                  <td>
                    <button type="button" className="open-chart" onClick={() => onOpen(item)}>
                      {item.id === "pt-athena" ? t("ward.openChart") : t("ward.view")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <p className="prototype-hint"><Sparkles size={14} /> {t("ward.demoHint")}</p>
    </main>
  );
}

function Dashboard({ onOpen, onGoRounds }: { onOpen: (patient: WardPatient) => void; onGoRounds: () => void }) {
  const { t, lang } = useLang();
  const total = wardPatients.length;
  const critical = wardPatients.filter((p) => p.status === "Critical").length;
  const watch = wardPatients.filter((p) => p.status === "Watch").length;
  const stable = wardPatients.filter((p) => p.status === "Stable").length;

  const metrics = [
    { label: t("dash.metric.admitted"), value: String(total), hint: t("dash.metric.admitted.hint"), icon: UsersRound },
    { label: t("dash.metric.notes"), value: "4", hint: t("dash.metric.notes.hint"), icon: WandSparkles },
    { label: t("dash.metric.gaps"), value: "2", hint: t("dash.metric.gaps.hint"), icon: ShieldCheck },
    { label: t("dash.metric.alerts"), value: "3", hint: t("dash.metric.alerts.hint"), icon: Bell },
  ];

  return (
    <main className="ward-page dashboard-page">
      <div className="ward-hero">
        <div>
          <span className="eyebrow"><LayoutDashboard size={14} /> {t("dash.overview")}</span>
          <h1>{t("nav.dashboard")}</h1>
          <p>{t("dash.sub")}</p>
        </div>
        <div className="round-status">
          <span>{t("dash.acuityMix")}</span>
          <strong>{critical} {t("dash.critical.suffix")}</strong>
          <div><i style={{ width: `${(critical / total) * 100}%`, background: "var(--error)" }} /></div>
          <small>{watch} {t("dash.watch.suffix")} · {stable} {t("dash.stable.suffix")}</small>
        </div>
      </div>

      <section className="metric-grid">
        {metrics.map((metric) => (
          <article className="metric-card" key={metric.label}>
            <div className="metric-icon"><metric.icon size={18} /></div>
            <strong>{metric.value}</strong>
            <span>{metric.label}</span>
            <small>{metric.hint}</small>
          </article>
        ))}
      </section>

      <div className="dashboard-columns">
        <section className="ward-card">
          <div className="ward-card-head">
            <div><h2>{t("dash.priority")}</h2><p>{t("dash.priority.sub")}</p></div>
            <button type="button" onClick={onGoRounds}><Stethoscope size={14} /> {t("dash.startRounds")}</button>
          </div>
          <div className="dash-patient-list">
            {[...wardPatients]
              .sort((a, b) => acuity(b.status) - acuity(a.status))
              .slice(0, 4)
              .map((item) => (
                <button type="button" className="dash-patient" key={item.id} onClick={() => onOpen(item)}>
                  <span className={`acuity-dot ${item.status.toLowerCase()}`} />
                  <span className="dash-patient-copy"><strong>{localizedName(lang, item.name, item.englishName)}</strong><small>{item.ward} {item.bed} · {item.diagnosis}</small></span>
                  <span className={`patient-status ${item.status.toLowerCase()}`}>{t(`status.${item.status.toLowerCase()}`)}</span>
                </button>
              ))}
          </div>
        </section>

        <section className="ward-card">
          <div className="ward-card-head"><div><h2>{t("dash.aiActivity")}</h2><p>{t("dash.aiActivity.sub")}</p></div></div>
          <ul className="activity-list">
            <li><Sparkles size={14} /> 12 {t("dash.activity.suggestions")}</li>
            <li><WandSparkles size={14} /> 5 {t("dash.activity.scribed")}</li>
            <li><ClipboardCheck size={14} /> 9 {t("dash.activity.orders")}</li>
            <li><ShieldCheck size={14} /> 2 {t("dash.activity.gaps")}</li>
          </ul>
        </section>
      </div>
    </main>
  );
}

function acuity(status: WardPatient["status"]) {
  return status === "Critical" ? 3 : status === "Watch" ? 2 : 1;
}

function RoundsView({ onOpen }: { onOpen: (patient: WardPatient) => void }) {
  const { t, lang } = useLang();
  return (
    <main className="ward-page">
      <div className="ward-hero">
        <div>
          <span className="eyebrow"><Stethoscope size={14} /> {t("rounds.morning")}</span>
          <h1>{t("nav.rounds")}</h1>
          <p>{t("rounds.sub")}</p>
        </div>
        <div className="round-status">
          <span>{t("rounds.progress")}</span>
          <strong>2 / {wardPatients.length}</strong>
          <div><i style={{ width: `${(2 / wardPatients.length) * 100}%` }} /></div>
          <small>{t("rounds.reviewed")}</small>
        </div>
      </div>
      <section className="rounds-grid">
        {[...wardPatients]
          .sort((a, b) => acuity(b.status) - acuity(a.status))
          .map((item, index) => (
            <article className="round-card" key={item.id}>
              <div className="round-card-head">
                <span className="round-index">{index + 1}</span>
                <span className={`patient-status ${item.status.toLowerCase()}`}>{t(`status.${item.status.toLowerCase()}`)}</span>
              </div>
              <strong>{localizedName(lang, item.name, item.englishName)}</strong>
              <small>HN {item.hn} · {item.age}Y</small>
              <p>{item.ward} · {t("ward.bed")} {item.bed}</p>
              <p className="round-dx">{item.diagnosis}</p>
              <button type="button" className="open-chart" onClick={() => onOpen(item)}>
                {item.id === "pt-athena" ? t("ward.openChart") : t("ward.viewChart")}
              </button>
            </article>
          ))}
      </section>
    </main>
  );
}

export function ProgressNoteApp() {
  const [activeTab, setActiveTab] = useState<Tab>("patients");
  const { t } = useLang();
  const [search, setSearch] = useState("");
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [soap, setSoap] = useState<SoapState>(initialSoap);
  const [suggestions, setSuggestions] = useState<GhostSuggestion[]>(ghostSuggestions);
  const [focusedField, setFocusedField] = useState<SoapSection>("subjective");
  const [orders, setOrders] = useState<ParsedOrder[]>([]);
  const [takeOrder, setTakeOrder] = useState(false);
  const [responsibleDoctor, setResponsibleDoctor] = useState("Dr. Kittisak S.");
  const [dateTime, setDateTime] = useState("2026-07-24T11:30");
  const [dictation, setDictation] = useState<DictationState>("idle");
  const [dictationTarget, setDictationTarget] = useState<DictationTarget>("subjective");
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [auditOpen, setAuditOpen] = useState(false);
  const [audits, setAudits] = useState<AuditEntry[]>(initialAudit);
  const [template, setTemplate] = useState<TemplatePreview | null>(null);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [customTemplates, setCustomTemplates] = useState<TemplatePreview[]>([]);
  const [templateBuilderOpen, setTemplateBuilderOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [finalized, setFinalized] = useState(false);
  const [ordersConfirmed, setOrdersConfirmed] = useState(false);
  const [encounterId, setEncounterId] = useState<string | null>(null);
  const [noteDraftId, setNoteDraftId] = useState<string | null>(null);
  const [autopilot, setAutopilot] = useState(false);
  // Output language for CARIVA notes — auto-selected from the device language.
  // No manual switch: CARIVA understands both Thai and English speech; this only
  // sets which language the written note comes out in.
  const [noteLang] = useState<"th" | "en">(() =>
    typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("th") ? "th" : "en",
  );
  const [aiSoap, setAiSoap] = useState<Record<SoapSection, boolean>>({
    subjective: false,
    objective: false,
    assessment: false,
    plan: false,
  });
  const [generatingFields, setGeneratingFields] = useState<SoapSection[]>([]);
  const recorderRef = useRef<AudioRecorder | null>(null);
  const [viewerCollapsed, setViewerCollapsed] = useState(false);
  const [soapCollapsed, setSoapCollapsed] = useState(false);
  const [orderCollapsed, setOrderCollapsed] = useState(false);
  const [orderResetVersion, setOrderResetVersion] = useState(0);
  const [dragLane, setDragLane] = useState<OrderLane | null>(null);

  const oneDayOrders = useMemo(() => orders.filter((order) => order.lane === "one-day"), [orders]);
  const continuousOrders = useMemo(() => orders.filter((order) => order.lane === "continuous"), [orders]);

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return wardPatients.filter((patient) =>
      [patient.name, patient.hn, patient.an, patient.diagnosis, patient.ward, patient.bed]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [search]);

  useEffect(() => {
    setCustomTemplates(loadCustomTemplates());
  }, []);

  useEffect(() => {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
    const timer = window.setTimeout(() => setDateTime(local), 0);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (dictation !== "recording") return;
    const timer = window.setInterval(() => setRecordingSeconds((seconds) => seconds + 1), 1000);
    return () => window.clearInterval(timer);
  }, [dictation]);

  function showToast(message: string) {
    setToast(message);
    window.setTimeout(() => setToast(null), 3600);
  }

  /** Auto-pilot: write AI text straight into fields, marked as AI-authored. */
  function autofillSoap(next: Partial<Record<SoapSection, string>>) {
    const fields = (Object.keys(next) as SoapSection[]).filter((field) => next[field]?.trim());
    if (!fields.length) return;
    setSoap((current) => ({ ...current, ...next }));
    setAiSoap((current) => {
      const updated = { ...current };
      for (const field of fields) updated[field] = true;
      return updated;
    });
    setSuggestions((current) => current.filter((item) => !fields.includes(item.field)));
    setGeneratingFields(fields);
    window.setTimeout(() => setGeneratingFields([]), 1300);
  }

  function editSoapField(field: SoapSection, value: string) {
    setSoap((current) => ({ ...current, [field]: value }));
    // Manual edit converts an AI-authored field back to a human-authored one.
    setAiSoap((current) => (current[field] ? { ...current, [field]: false } : current));
  }

  function toggleAutopilot() {
    const next = !autopilot;
    setAutopilot(next);
    if (next && suggestions.length) {
      // Apply any pending suggestions immediately.
      const merged: Partial<Record<SoapSection, string>> = {};
      for (const suggestion of suggestions) {
        merged[suggestion.field] = [soap[suggestion.field], suggestion.text].filter(Boolean).join(" ");
      }
      autofillSoap(merged);
    }
    setAudits((current) => [
      {
        id: `autopilot-${Date.now()}`,
        at: nowLabel(),
        user: "Dr. Heartbeat",
        action: next ? "Auto-pilot enabled" : "Auto-pilot disabled",
        diff: next
          ? "AI suggestions now auto-fill SOAP and draft orders; AI-authored content is visually flagged and remains editable."
          : "Returned to review-before-accept mode.",
        takeOrder,
        provenance: "User-entered",
      },
      ...current,
    ]);
    showToast(next ? t("toast.autopilotOn") : t("toast.autopilotOff"));
  }

  function openChart(patient: WardPatient) {
    setSearch("");
    if (patient.id === "pt-athena") {
      setWorkspaceOpen(true);
      // Best-effort: resolve a real Aurora encounter so saves persist server-side.
      void ensureEncounter().then((id) => {
        if (id) setEncounterId(id);
      });
      return;
    }
    setActiveTab("patients");
    showToast(`${patient.name}: ${t("toast.demoChart")}`);
  }

  /** Persist the SOAP note + confirmation to Aurora. Returns a short label or null. */
  async function persistNote(): Promise<string | null> {
    if (!encounterId) return null;
    const saved = await saveNoteApi(encounterId, soap, noteDraftId ?? undefined);
    if (!saved) return null;
    setNoteDraftId(saved.noteDraftId);
    const conf = await confirmClinical(
      encounterId,
      "note",
      saved.noteDraftId,
      { sections: soap },
      `note-${saved.contentHash}`,
      takeOrder,
    );
    if (!conf) return null;
    return `hash ${saved.contentHash.slice(0, 12)}…${conf.idempotentReplay ? " (idempotent replay)" : ""}`;
  }

  /** Persist the order draft + items + confirmation to Aurora. */
  async function persistOrders(): Promise<string | null> {
    if (!encounterId || !orders.length) return null;
    const saved = await saveOrdersApi(encounterId, orders, takeOrder);
    if (!saved) return null;
    const conf = await confirmClinical(
      encounterId,
      "order",
      saved.orderDraftId,
      { orders: orders.map((o) => ({ label: o.label, details: o.details, lane: o.lane, stat: o.stat })) },
      `order-${saved.orderDraftId}`,
      takeOrder,
    );
    if (!conf) return null;
    return `${saved.itemCount} item(s) · hash ${conf.confirmation.contentHash.slice(0, 12)}…`;
  }

  function appendServerAudit(action: string, diff: string) {
    setAudits((current) => [
      {
        id: `srv-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        at: nowLabel(),
        user: "Sati App API",
        action,
        diff,
        takeOrder,
        provenance: "User-entered",
      },
      ...current,
    ]);
  }

  function goHome() {
    setWorkspaceOpen(false);
    setActiveTab("patients");
    setSearch("");
  }

  function navigate(tab: Tab) {
    setWorkspaceOpen(false);
    setActiveTab(tab);
    setSearch("");
  }

  function acceptSuggestion(suggestion: GhostSuggestion) {
    const doctorInput = soap[suggestion.field] || "No doctor-entered text before suggestion";
    const selectedOutput = [soap[suggestion.field], suggestion.text].filter(Boolean).join(" ");
    setSoap((current) => ({
      ...current,
      [suggestion.field]: selectedOutput,
    }));
    setAiSoap((current) => ({ ...current, [suggestion.field]: true }));
    setSuggestions((current) => current.filter((item) => item.field !== suggestion.field));
    setAudits((current) => [
      {
        id: `ai-${Date.now()}`,
        at: nowLabel(),
        user: "Dr. Heartbeat",
        action: `Heartbeat accepted in ${suggestion.field}`,
        diff: `+ ${suggestion.text}`,
        takeOrder,
        provenance: "AI-suggested → accepted",
        userInput: doctorInput,
        aiSuggestion: suggestion.text,
        selectedOutput,
        evidence: suggestion.evidence,
        model: suggestion.model ?? "Nong SATI Heartbeat v2",
      },
      ...current,
    ]);
    showToast(`${t("toast.addedTo")} ${t(`soap.${suggestion.field}`)} — ${t("toast.evidenceAttached")}`);
  }

  function acceptAllSuggestions() {
    if (!suggestions.length) return;
    setSoap((current) => {
      const next = { ...current };
      for (const suggestion of suggestions) {
        next[suggestion.field] = [next[suggestion.field], suggestion.text].filter(Boolean).join(" ");
      }
      return next;
    });
    const count = suggestions.length;
    setAudits((current) => [
      {
        id: `ai-all-${Date.now()}`,
        at: nowLabel(),
        user: "Dr. Heartbeat",
        action: `Accepted all ${count} Heartbeat suggestions`,
        diff: `${count} evidence-grounded suggestions merged into the SOAP note.`,
        takeOrder,
        provenance: "AI-suggested → accepted",
      },
      ...current,
    ]);
    setSuggestions([]);
    showToast(`${t("toast.acceptedAllPre")} ${count} ${t("toast.acceptedAllPost")}`);
  }

  function addOrders(newOrders: ParsedOrder[], audit?: AiAuditContext) {
    const attributedOrders = newOrders.map((order) => ({
      ...order,
      orderedBy: order.orderedBy ?? "Dr. Heartbeat",
      orderedAt: order.orderedAt ?? nowLabel(),
    }));
    setOrders((current) => [...current, ...attributedOrders]);
    setOrdersConfirmed(false);
    if (audit) {
      setAudits((current) => [
        {
          id: `order-ai-${Date.now()}`,
          at: nowLabel(),
          user: "Dr. Heartbeat",
          action: audit.action,
          diff: `${newOrders.length} draft order${newOrders.length > 1 ? "s" : ""} created for clinician review.`,
          takeOrder,
          provenance: audit.action.toLowerCase().includes("dictat")
            ? "AI-dictated → confirmed"
            : "AI-suggested → accepted",
          userInput: audit.userInput,
          aiSuggestion: audit.aiSuggestion,
          selectedOutput: audit.selectedOutput,
          evidence: audit.evidence,
          model: audit.model,
        },
        ...current,
      ]);
    }
    showToast(`${newOrders.length} ${t("toast.ordersAdded")}`);
  }

  function updateOrder(id: string, patch: Partial<ParsedOrder>) {
    setOrders((current) => current.map((order) => (order.id === id ? { ...order, ...patch } : order)));
    setOrdersConfirmed(false);
  }

  function deleteOrder(id: string) {
    setOrders((current) => current.filter((order) => order.id !== id));
    setOrdersConfirmed(false);
  }

  async function approveOrders() {
    if (!orders.length) {
      showToast(t("toast.noOrders"));
      return;
    }
    setOrdersConfirmed(true);
    const stamp = nowLabel();
    setOrders((current) => current.map((order) => ({ ...order, orderedAt: order.orderedAt ?? stamp })));
    setAudits((current) => [
      {
        id: `orders-confirm-${Date.now()}`,
        at: stamp,
        user: takeOrder ? "Dr. Heartbeat (covering)" : "Dr. Heartbeat",
        action: `Orders approved & saved (${orders.length})`,
        diff: `${oneDayOrders.length} one-day/STAT and ${continuousOrders.length} continuous order(s) confirmed for HOSxP write-back.`,
        takeOrder,
        provenance: "User-entered",
      },
      ...current,
    ]);
    const persisted = await persistOrders();
    if (persisted) {
      appendServerAudit("Orders persisted to Aurora", `Order draft + items written and confirmed · ${persisted}.`);
      showToast(`${orders.length} ${t("toast.ordersApprovedSaved")} · ${persisted}`);
    } else {
      showToast(`${orders.length} ${t("toast.ordersApproved")}`);
    }
  }

  function clearSoapNote() {
    setSoap({ subjective: "", objective: "", assessment: "", plan: "" });
    setSuggestions([]);
    setFinalized(false);
    setDictation("idle");
    setRecordingSeconds(0);
    setAudits((current) => [
      {
        id: `clear-soap-${Date.now()}`,
        at: nowLabel(),
        user: "Dr. Heartbeat",
        action: "SOAP workspace reset",
        diff: "All SOAP text and pending AI suggestions were cleared.",
        takeOrder,
        provenance: "User-entered",
      },
      ...current,
    ]);
    showToast(t("toast.soapCleared"));
  }

  function clearOrders() {
    setOrders([]);
    setOrdersConfirmed(false);
    setOrderResetVersion((version) => version + 1);
    setAudits((current) => [
      {
        id: `clear-orders-${Date.now()}`,
        at: nowLabel(),
        user: "Dr. Heartbeat",
        action: "Order workspace reset",
        diff: "All draft orders, typed order text, and pending order-dictation review were cleared.",
        takeOrder,
        provenance: "User-entered",
      },
      ...current,
    ]);
    showToast(t("toast.ordersCleared"));
  }

  function startOrderDrag(event: DragEvent<HTMLDivElement>, id: string) {
    event.dataTransfer.setData("text/plain", id);
    event.dataTransfer.effectAllowed = "move";
  }

  function convertPlanSuggestion() {
    const planText = soap.plan?.trim();
    if (!planText) {
      showToast(t("toast.planEmpty"));
      return;
    }
    const parsed = parseOrdersFromPlan(planText);
    if (!parsed.length) {
      showToast(t("toast.noOrderable"));
      return;
    }
    const convertedOrders: ParsedOrder[] = parsed.map((order, index) => ({
      id: `plan-${Date.now()}-${index}`,
      category: order.category,
      label: order.label,
      details: order.details || "From Plan",
      lane: order.lane,
      stat: order.stat,
      source: "agent",
      aiGenerated: true,
    }));
    addOrders(convertedOrders, {
      action: "Converted Plan (P) to Doctor Orders",
      userInput: planText,
      aiSuggestion: parsed.map((o) => o.label).join(", "),
      selectedOutput: `${convertedOrders.length} draft order(s) parsed from the Plan and routed to lanes`,
      evidence: "Derived from the SOAP Plan slot",
      model: "Plan parser (rule-based)",
    });
    showToast(`${convertedOrders.length} ${t("toast.ordersFromPlan")}`);
  }

  function sendHeartbeatToSoap(action: { field: SoapSection; text: string; title: string; evidence: string }) {
    const doctorInput = soap[action.field] || "No doctor-entered text before Heartbeat suggestion";
    setSoapCollapsed(false);
    setFocusedField(action.field);
    // Always write into the SOAP text box (merged with any typed text) and flag
    // it AI-authored — same behaviour whether or not auto-pilot is on.
    autofillSoap({ [action.field]: [soap[action.field]?.trim(), action.text].filter(Boolean).join("\n") });
    setAudits((current) => [
      {
        id: `feed-soap-${Date.now()}-${action.field}`,
        at: nowLabel(),
        user: "Dr. Heartbeat",
        action: `${action.title} written to ${action.field}`,
        diff: "AI wording written into the note and flagged as AI-authored (blue) for review; editable by the clinician.",
        takeOrder,
        provenance: "AI-suggested → accepted",
        userInput: doctorInput,
        aiSuggestion: action.text,
        selectedOutput: `Written into ${action.field}`,
        evidence: action.evidence,
        model: "Nong SATI Heartbeat v2",
      },
      ...current,
    ]);
  }

  function draftHeartbeatOrder(order: ParsedOrder, source: { title: string; evidence: string }) {
    setOrderCollapsed(false);
    addOrders([{ ...order, aiGenerated: true }], {
      action: `${source.title} converted to a draft order`,
      userInput: `Heartbeat Feed action selected: ${source.title}`,
      aiSuggestion: `${order.label} — ${order.details}`,
      selectedOutput: `${order.label} staged in ${order.lane === "continuous" ? "Continuous" : "One-Day"} lane`,
      evidence: source.evidence,
      model: "Nong SATI Heartbeat v2",
    });
  }

  function toggleTakeOrder() {
    const next = !takeOrder;
    setTakeOrder(next);
    setAudits((current) => [
      {
        id: `take-${Date.now()}`,
        at: nowLabel(),
        user: "Dr. Heartbeat",
        action: next ? "Take Order mode enabled" : "Take Order mode disabled",
        diff: next ? `Ordering on behalf of ${responsibleDoctor}; both identities stamped.` : "Returned to direct attribution.",
        takeOrder: next,
        provenance: "User-entered",
      },
      ...current,
    ]);
    showToast(next ? t("toast.takeOrderOn") : t("toast.takeOrderOff"));
  }

  async function startDictation(target: DictationTarget) {
    setDictationTarget(target);
    setSoapCollapsed(false);
    if (target === "all") {
      // Begin real microphone capture for the CARIVA ASR Scribe flow.
      const recorder = new AudioRecorder();
      const started = await recorder.start();
      recorderRef.current = started ? recorder : null;
      setDictation("recording");
      setRecordingSeconds(0);
      showToast(
        started
          ? t("toast.scribeRecording")
          : t("toast.micUnavailable"),
      );
      return;
    }
    setDictation("recording");
    setRecordingSeconds(0);
    showToast(`${t("toast.recordingForPre")} ${t(`soap.${target}`)} — ${t("toast.recordingForPost")}`);
  }

  async function stopDictation() {
    setDictation("processing");

    if (dictationTarget === "all") {
      // 1) Stop recording and (if configured) transcribe via CARIVA ASR.
      //    CARIVA's doc-ipd-soap mode returns the raw transcript AND the four
      //    structured SOAP sections. Doctor Orders are NOT returned by CARIVA,
      //    so we parse those from the transcript in step (2).
      let transcript = "";
      let carivaSoap: Record<SoapSection, string> | null = null;
      let usedCariva = false;
      const recorder = recorderRef.current;
      recorderRef.current = null;
      if (recorder) {
        const blob = await recorder.stop();
        if (blob) {
          const result = await transcribeWithCariva(blob, noteLang); // browser → gateway → CARIVA
          if (result) {
            transcript = result.transcript;
            carivaSoap = result.soap;
            usedCariva = true;
          }
        }
      }

      // 2) Parse Doctor Orders from the dictation. CARIVA's Plan ("P") section
      //    is where orders usually live, so feed it into the parser alongside
      //    the raw transcript for stronger order extraction. Prefer CARIVA's
      //    structured SOAP for the note; take Doctor Orders (one-day/continuous
      //    lanes) from our own parser.
      const orderSource = [transcript, carivaSoap?.plan ? `Plan: ${carivaSoap.plan}` : ""]
        .filter(Boolean)
        .join("\n\n");
      const parsed = await structureEncounter(orderSource || transcript);
      const structured = {
        soap: carivaSoap ?? parsed.soap,
        orders: parsed.orders,
        mode: (usedCariva ? "live" : parsed.mode) as "live" | "simulation",
      };
      const sourceLabel = usedCariva ? "CARIVA ASR" : structured.mode === "live" ? "Nong SATI (Bedrock)" : "simulation";

      // 3a) SOAP → ALWAYS write into the text boxes with the AI-authored
      //     (auto-pilot) effect, in both modes. Merge with any text the doctor
      //     already typed — never replace it.
      const merged: Partial<Record<SoapSection, string>> = {};
      for (const field of Object.keys(structured.soap) as SoapSection[]) {
        const incoming = structured.soap[field]?.trim();
        if (incoming) merged[field] = [soap[field]?.trim(), incoming].filter(Boolean).join("\n");
      }
      autofillSoap(merged);

      // 3b) Doctor Orders → parsed from the Plan ("P") slot and drafted into lanes.
      const planForOrders = structured.soap.plan;
      const scribeOrders: ParsedOrder[] = parseOrdersFromPlan(planForOrders).map((order, index) => ({
        id: `scribe-${Date.now()}-${index}`,
        category: order.category,
        label: order.label,
        details: order.details || "From Plan",
        lane: order.lane,
        stat: order.stat,
        source: "agent",
        aiGenerated: true,
      }));
      if (scribeOrders.length) {
        addOrders(scribeOrders, {
          action: "Scribe Doctor Orders from the Plan (P)",
          userInput: planForOrders || transcript || "Ward-round dictation",
          aiSuggestion: scribeOrders.map((order) => order.label).join(", "),
          selectedOutput: `${scribeOrders.length} draft order(s) parsed from the Plan and routed to lanes`,
          evidence: `Ward-round dictation · ${sourceLabel}`,
          model: "Plan parser (rule-based)",
        });
      }

      setAudits((current) => [
        {
          id: `scribe-${Date.now()}`,
          at: nowLabel(),
          user: "Dr. Heartbeat",
          action: `Full SOAP scribed from one dictation (${sourceLabel})`,
          diff: `${Object.values(structured.soap).filter(Boolean).length} SOAP field(s) auto-filled (flagged as AI for review) and ${scribeOrders.length} order(s) drafted.`,
          takeOrder,
          provenance: "AI-dictated → staged",
          evidence: `Ward-round dictation · ${sourceLabel}`,
          model: "Nong SATI Scribe",
        },
        ...current,
      ]);
      setDictation("idle");
      showToast(`${t("toast.scribeFilled")} + ${scribeOrders.length} ${t("toast.orders")} · ${sourceLabel}`);
      return;
    }

    const suggestion = await processSoapDictation(dictationTarget);
    if (autopilot) {
      autofillSoap({ [dictationTarget]: suggestion.text });
    } else {
      setSuggestions((current) => [
        ...current.filter((item) => item.field !== dictationTarget),
        suggestion,
      ]);
    }
    setDictation("idle");
    showToast(`${t("toast.dictationReady")} ${t(`soap.${dictationTarget}`)} · ${suggestion.mode === "live" ? t("order.entry.live") : t("order.entry.sim")}`);
  }

  async function selectTemplate(id: string) {
    if (!id) return;
    setTemplateOpen(true);
    setTemplateLoading(true);
    const loaded = await loadTemplate(id);
    setTemplate(loaded);
    setTemplateLoading(false);
  }

  function commitTemplate(selected: TemplatePreview) {
    setSoap((current) => ({ ...current, ...selected.soap }));
    const safeOrders = selected.orders
      .filter((order) => !order.conflict)
      .map((order) => ({
        ...order,
        id: `${order.id}-${Date.now()}`,
        orderedBy: "Dr. Heartbeat",
        orderedAt: nowLabel(),
      }));
    setOrders((current) => [...current, ...safeOrders]);
    const conflicts = selected.orders.filter((order) => order.conflict).length;
    setAudits((current) => [
      {
        id: `tpl-${Date.now()}`,
        at: nowLabel(),
        user: "Dr. Heartbeat",
        action: `${selected.name} template confirmed`,
        diff: `SOAP pre-populated; ${safeOrders.length} safe orders added; ${conflicts} conflict(s) excluded.`,
        takeOrder,
        provenance: "Template → confirmed",
      },
      ...current,
    ]);
    setTemplateOpen(false);
    showToast(conflicts ? `${t("toast.templateAddedConflictPre")} ${conflicts} ${t("toast.templateAddedConflictPost")}` : t("toast.templateAdded"));
  }

  function handleSaveTemplate(created: TemplatePreview) {
    const next = saveCustomTemplate(created);
    setCustomTemplates(next);
    setTemplateBuilderOpen(false);
    setAudits((current) => [
      {
        id: `tpl-new-${Date.now()}`,
        at: nowLabel(),
        user: "Dr. Heartbeat",
        action: `Template "${created.name}" created`,
        diff: `${Object.keys(created.soap).length} SOAP field(s) and ${created.orders.length} order(s) saved as a reusable template.`,
        takeOrder,
        provenance: "User-entered",
      },
      ...current,
    ]);
    showToast(`${t("toast.templateSavedPre")} “${created.name}” ${t("toast.templateSavedPost")}`);
  }

  async function finalizeNote() {
    if (finalized) return;
    setFinalized(true);
    setAudits((current) => [
      {
        id: `final-${Date.now()}`,
        at: nowLabel(),
        user: "Dr. Heartbeat",
        action: "Clinical note finalized",
        diff: `${Object.values(soap).filter(Boolean).length} SOAP fields and ${orders.length} orders signed for simulated HOSxP write-back.`,
        takeOrder,
        provenance: "User-entered",
      },
      ...current,
    ]);
    const persisted = await persistNote();
    if (persisted) {
      appendServerAudit("Note persisted to Aurora", `SOAP version written and confirmed · ${persisted}.`);
      showToast(`${t("toast.noteFinalizedPersisted")} · ${persisted}`);
    } else {
      showToast(t("toast.noteFinalized"));
    }
  }

  async function saveAllAndReturn() {
    setFinalized(true);
    if (orders.length) setOrdersConfirmed(true);
    const filledSoap = Object.values(soap).filter(Boolean).length;
    setAudits((current) => [
      {
        id: `encounter-save-${Date.now()}`,
        at: nowLabel(),
        user: takeOrder ? "Dr. Heartbeat (covering)" : "Dr. Heartbeat",
        action: "Encounter saved & closed",
        diff: `${filledSoap} SOAP field(s) finalized and ${orders.length} order(s) confirmed for HOSxP write-back; returned to ward list.`,
        takeOrder,
        provenance: "User-entered",
      },
      ...current,
    ]);
    // Persist to Aurora before leaving (best-effort).
    const [noteInfo, orderInfo] = await Promise.all([persistNote(), persistOrders()]);
    if (noteInfo || orderInfo) {
      appendServerAudit(
        "Encounter persisted to Aurora",
        [noteInfo ? `Note ${noteInfo}` : null, orderInfo ? `Orders ${orderInfo}` : null].filter(Boolean).join(" · "),
      );
    }
    setWorkspaceOpen(false);
    setActiveTab("patients");
    showToast(
      noteInfo || orderInfo
        ? `${t("toast.savedAuroraPre")} ${filledSoap} ${t("toast.soapFields")}, ${orders.length} ${t("toast.orders")}. ${t("toast.returnedWard")}`
        : `${t("toast.savedPre")} ${filledSoap} ${t("toast.soapFields")}, ${orders.length} ${t("toast.orders")}. ${t("toast.returnedWard")}`,
    );
  }

  const formattedRecording = `${String(Math.floor(recordingSeconds / 60)).padStart(2, "0")}:${String(recordingSeconds % 60).padStart(2, "0")}`;
  const dictationLabel = dictationTarget === "all" ? t("soap.allFull") : t(`soap.${dictationTarget}`);

  return (
    <div className="app-shell">
      <TopNav
        activeTab={activeTab}
        onNavigate={navigate}
        onHome={goHome}
        search={search}
        onSearch={setSearch}
        results={searchResults}
        onSelectResult={openChart}
      />
      {activeTab === "patients" && <WardList onOpen={openChart} />}
      {activeTab === "dashboard" && <Dashboard onOpen={openChart} onGoRounds={() => navigate("rounds")} />}
      {activeTab === "rounds" && <RoundsView onOpen={openChart} />}

      {workspaceOpen && (
        <div className="clinical-workspace">
          <TopNav
            activeTab={activeTab}
            onNavigate={navigate}
            onHome={goHome}
            search={search}
            onSearch={setSearch}
            results={searchResults}
            onSelectResult={openChart}
          />
          <PatientBanner takeOrder={takeOrder} responsibleDoctor={responsibleDoctor} />

          <section className="order-header">
            <button type="button" className="back-button" onClick={() => setWorkspaceOpen(false)}><ChevronLeft size={15} /> Ward list</button>
            <label><CalendarDays size={14} /><span>Date & time</span><input type="datetime-local" value={dateTime} onChange={(event) => setDateTime(event.target.value)} /></label>
            <label><Stethoscope size={14} /><span>Responsible doctor</span>
              <select value={responsibleDoctor} onChange={(event) => setResponsibleDoctor(event.target.value)}>
                <option>Dr. Kittisak S.</option>
                <option>Dr. Heartbeat</option>
                <option>Dr. Anong P.</option>
              </select>
            </label>
            <button
              type="button"
              className={`take-toggle ${takeOrder ? "on" : ""}`}
              onClick={toggleTakeOrder}
              aria-pressed={takeOrder}
              title="Use when you enter orders on behalf of the responsible doctor. Both doctors and the timestamp are stored in the audit trail."
            >
              <span><ShieldCheck size={15} /> Take Order (covering)</span><i><b /></i>
            </button>
            <div className="take-order-explainer" title="Use when entering orders on behalf of the responsible doctor. Both identities and the timestamp are stored in the audit trail.">
              <HelpCircle size={15} />
              <span><strong>Covering doctor mode</strong> Both doctors are recorded in the audit trail.</span>
            </div>

            <select className="template-select" value="" onChange={(event) => void selectTemplate(event.target.value)} aria-label="Clinical templates">
              <option value="" disabled>Templates</option>
              <optgroup label="Built-in">
                <option value="post-op">Post-Op Routine</option>
                <option value="sepsis">PCT Sepsis Pathway</option>
                <option value="diabetes">Diabetes Care</option>
              </optgroup>
              {customTemplates.length > 0 && (
                <optgroup label="My templates">
                  {customTemplates.map((tpl) => (
                    <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                  ))}
                </optgroup>
              )}
            </select>
            <button type="button" className="new-template-button" onClick={() => setTemplateBuilderOpen(true)} title="Save the current note and orders as a reusable template">
              <FilePlus2 size={15} /> New template
            </button>
            <button type="button" className="audit-button" onClick={() => setAuditOpen(true)}><FileClock size={15} /> Access Audit Log</button>
            <button type="button" className="save-return-button" onClick={saveAllAndReturn} title="Finalize the note, confirm all orders, and return to the ward list">
              <ClipboardCheck size={15} /> Save &amp; return to ward
            </button>
          </section>

          <main className={`progress-grid ${viewerCollapsed ? "viewer-hidden" : ""}`}>
            <aside className={`left-rail ${viewerCollapsed ? "is-collapsed" : ""}`}>
              <ClinicalViewer collapsed={viewerCollapsed} onToggle={() => setViewerCollapsed((current) => !current)} />
              {!viewerCollapsed && (
                <div className="mission-card">
                  <span>RECORD READINESS</span>
                  <strong>78%</strong>
                  <div><i style={{ width: "78%" }} /></div>
                  <p>2 claim-critical evidence gaps remain before discharge.</p>
                </div>
              )}
            </aside>

            <div className="center-column">
              <section className={`panel soap-panel ${soapCollapsed ? "is-collapsed" : ""}`}>
                <div className="panel-heading soap-panel-heading">
                  <div><WandSparkles size={18} /><span>{t("soap.title")}</span></div>
                  <div>
                    {!soapCollapsed && (
                      <div className="soap-dictation-control">
                        {dictation === "idle" ? (
                          <>
                            <button type="button" className="soap-scribe-start" onClick={() => void startDictation("all")} title="Record once (CARIVA ASR); structures all four SOAP fields + Doctor Orders">
                              <AudioLines size={14} /> {t("soap.scribe")}
                            </button>
                            <button type="button" className="soap-dictation-start" onClick={() => void startDictation(focusedField)}>
                              <Mic size={14} /> {t("soap.dictate")} {t(`soap.${focusedField}`)}
                            </button>
                          </>
                        ) : dictation === "processing" ? (
                          <button type="button" className="soap-dictation-processing" disabled>
                            <LoaderCircle size={14} className="spin" /> {t("soap.structuring")} {dictationLabel}…
                          </button>
                        ) : (
                          <>
                            <span className="recording-indicator">● {formattedRecording} {dictation === "recording" ? "RECORDING" : "PAUSED"}</span>
                            <button type="button" onClick={() => setDictation(dictation === "recording" ? "paused" : "recording")} aria-label={dictation === "recording" ? "Pause dictation" : "Resume dictation"}>
                              {dictation === "recording" ? <Pause size={14} /> : <Play size={14} />}
                            </button>
                            <button type="button" onClick={() => void stopDictation()} aria-label="Stop dictation and prepare suggestion"><Square size={13} /> Stop &amp; review</button>
                          </>
                        )}
                      </div>
                    )}
                    {!soapCollapsed && (
                      <button
                        type="button"
                        className={`autopilot-toggle ${autopilot ? "on" : ""}`}
                        onClick={toggleAutopilot}
                        aria-pressed={autopilot}
                        title="Auto-pilot: AI auto-fills SOAP and drafts orders without a manual accept step. AI-authored content is flagged in blue and stays editable."
                      >
                        <Sparkles size={13} /> Auto-pilot {autopilot ? "ON" : "OFF"}
                      </button>
                    )}
                    {!soapCollapsed && suggestions.length > 0 && (
                      <button type="button" className="accept-all-button" onClick={acceptAllSuggestions} title="Accept every pending suggestion into the note">
                        <Check size={13} /> Accept all ({suggestions.length})
                      </button>
                    )}
                    {!soapCollapsed && <span className="autosave"><Check size={12} /> Autosaved just now</span>}
                    {!soapCollapsed && <span className="author-chip">Doctor is final author</span>}
                    <button type="button" className="section-toggle" onClick={() => setSoapCollapsed((current) => !current)} aria-expanded={!soapCollapsed}>
                      {soapCollapsed ? <Eye size={15} /> : <EyeOff size={15} />} {soapCollapsed ? "Show note" : "Hide note"}
                    </button>
                  </div>
                </div>
                {!soapCollapsed && (
                  <>
                    {dictation !== "idle" && (
                      <div className={`dictation-session-banner ${dictation}`}>
                        {dictation === "processing" ? <LoaderCircle size={18} className="spin" /> : <AudioLines size={18} />}
                        <div>
                          <strong>{dictation === "processing" ? `Structuring your ${dictationLabel} dictation…` : (dictationTarget === "all" ? "Scribing full SOAP note" : `Recording ${dictationTarget}`)}</strong>
                          <span>{dictation === "processing" ? "Heartbeat is preparing optional text for your review." : (dictationTarget === "all" ? "One recording will be split into Subjective, Objective, Assessment, and Plan." : "The note remains unchanged. Stop when ready to review the suggestion.")}</span>
                        </div>
                        {dictation !== "processing" && <span className="dictation-wave" aria-hidden="true"><i /><i /><i /><i /><i /></span>}
                      </div>
                    )}
                    <div className="soap-grid">
                      {([
                        ["subjective", t("soap.subjective"), t("soap.subjective.hint")],
                        ["objective", t("soap.objective"), t("soap.objective.hint")],
                        ["assessment", t("soap.assessment"), t("soap.assessment.hint")],
                        ["plan", t("soap.plan"), t("soap.plan.hint")],
                      ] as [SoapSection, string, string][]).map(([id, label, hint]) => (
                        <SoapCard
                          key={id}
                          id={id}
                          label={label}
                          hint={hint}
                          value={soap[id]}
                          suggestion={suggestions.find((item) => item.field === id)}
                          isFocused={focusedField === id}
                          aiAuthored={aiSoap[id]}
                          generating={generatingFields.includes(id)}
                          onFocus={setFocusedField}
                          onChange={editSoapField}
                          onAcceptSuggestion={acceptSuggestion}
                          onDismissSuggestion={(field) => setSuggestions((current) => current.filter((item) => item.field !== field))}
                        >
                          {id === "plan" && (
                            <div className="convert-card">
                              <div><Sparkles size={15} /><span><strong>{t("soap.convertPlan")}</strong><small>{t("soap.convertPlan.hint")}</small></span></div>
                              <button type="button" onClick={convertPlanSuggestion}>{t("soap.convert.btn")}</button>
                            </div>
                          )}
                        </SoapCard>
                      ))}
                    </div>
                    <div className="soap-footer">
                      <button type="button" className="clear-button" onClick={clearSoapNote}><Trash2 size={14} /> {t("soap.clear")}</button>
                      <div className="footer-evidence"><ShieldCheck size={14} /> {t("soap.footer.evidence")}</div>
                      <button type="button" className={`finalize-button ${finalized ? "is-finalized" : ""}`} onClick={finalizeNote}>
                        {finalized ? <><Check size={16} /> {t("soap.finalized")}</> : <><ClipboardCheck size={16} /> {t("soap.finalize")}</>}
                      </button>
                    </div>
                  </>
                )}
              </section>

              <section className={`panel order-panel ${orderCollapsed ? "is-collapsed" : ""}`}>
                <div className="panel-heading order-panel-heading">
                  <div><Sparkles size={18} /><span>Intelligent Order Entry</span></div>
                  <div>
                    {!orderCollapsed && <span>Hybrid parser · review before signing</span>}
                    <button type="button" className="section-clear-button" onClick={clearOrders}>
                      <Trash2 size={14} /> Clear orders
                    </button>
                    <button type="button" className="section-toggle" onClick={() => setOrderCollapsed((current) => !current)} aria-expanded={!orderCollapsed}>
                      {orderCollapsed ? <Eye size={15} /> : <EyeOff size={15} />} {orderCollapsed ? "Show orders" : "Hide orders"}
                    </button>
                  </div>
                </div>
                {!orderCollapsed && (
                  <>
                    <OrderOmnibox key={orderResetVersion} onAddOrders={addOrders} />
                    <div className="order-lanes">
                      <div className="order-lane one-day">
                        <div className="lane-head"><span><Clock3 size={15} /> {t("order.oneDay")}</span><b>{oneDayOrders.length}</b></div>
                        <div
                          className={`lane-body ${dragLane === "one-day" ? "drag-over" : ""}`}
                          onDragOver={(e) => { e.preventDefault(); if (dragLane !== "one-day") setDragLane("one-day"); }}
                          onDragLeave={(e) => { if (e.currentTarget === e.target) setDragLane(null); }}
                          onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain"); if (id) updateOrder(id, { lane: "one-day" }); setDragLane(null); }}
                        >
                          <LaneOrderSearch lane="one-day" onAddOrders={addOrders} />
                          {oneDayOrders.length ? oneDayOrders.map((order) => <OrderChip key={order.id} order={order} onChange={updateOrder} onDelete={deleteOrder} onDragStart={startOrderDrag} />) : (
                            <div className="empty-lane"><Clock3 size={22} /><strong>{t("order.oneDay.empty.title")}</strong><span>{t("order.oneDay.empty.hint")} {t("order.dragHint")}</span></div>
                          )}
                        </div>
                      </div>
                      <div className="order-lane continuous">
                        <div className="lane-head"><span><Activity size={15} /> {t("order.continuous")}</span><b>{continuousOrders.length}</b></div>
                        <div
                          className={`lane-body ${dragLane === "continuous" ? "drag-over" : ""}`}
                          onDragOver={(e) => { e.preventDefault(); if (dragLane !== "continuous") setDragLane("continuous"); }}
                          onDragLeave={(e) => { if (e.currentTarget === e.target) setDragLane(null); }}
                          onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain"); if (id) updateOrder(id, { lane: "continuous" }); setDragLane(null); }}
                        >
                          <LaneOrderSearch lane="continuous" onAddOrders={addOrders} />
                          {continuousOrders.length ? continuousOrders.map((order) => <OrderChip key={order.id} order={order} onChange={updateOrder} onDelete={deleteOrder} onDragStart={startOrderDrag} />) : (
                            <div className="empty-lane"><Activity size={22} /><strong>{t("order.continuous.empty.title")}</strong><span>{t("order.continuous.empty.hint")} {t("order.dragHint")}</span></div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="order-footer">
                      <span className="order-footer-status">
                        {ordersConfirmed
                          ? <><ShieldCheck size={14} /> {orders.length} order(s) approved &amp; saved</>
                          : <><Clock3 size={14} /> {orders.length} draft order(s) awaiting approval</>}
                      </span>
                      <button
                        type="button"
                        className={`approve-orders-button ${ordersConfirmed ? "is-confirmed" : ""}`}
                        onClick={approveOrders}
                        disabled={orders.length === 0}
                      >
                        {ordersConfirmed ? <><Check size={16} /> Orders saved</> : <><ClipboardCheck size={16} /> Approve &amp; Save Orders</>}
                      </button>
                    </div>
                  </>
                )}
              </section>
            </div>

            <aside className="right-rail">
              <AuditorPanel
                soap={soap}
                orders={orders}
                onFocusField={(field) => {
                  setSoapCollapsed(false);
                  setFocusedField(field);
                }}
                onSendToSoap={sendHeartbeatToSoap}
                onDraftOrder={draftHeartbeatOrder}
              />
              <HelpDeskPanel />
              <HeartbeatFeed onSendToSoap={sendHeartbeatToSoap} onDraftOrder={draftHeartbeatOrder} />
            </aside>
          </main>

          <footer className="workspace-footer">
            <span><LockKeyhole size={12} /> Simulated hospital environment · no real patient data</span>
            <div><span className="connected-dot" /> WebSocket connected <span>•</span> <strong>HOSxP overlay</strong></div>
          </footer>

          <AuditDrawer open={auditOpen} entries={audits} onClose={() => setAuditOpen(false)} />
          {templateOpen && (
            <TemplateModal
              template={template}
              loading={templateLoading}
              onClose={() => setTemplateOpen(false)}
              onCommit={commitTemplate}
            />
          )}
          {templateBuilderOpen && (
            <TemplateBuilderModal
              initialSoap={soap}
              initialOrders={orders}
              onClose={() => setTemplateBuilderOpen(false)}
              onSave={handleSaveTemplate}
            />
          )}
          {toast && <div className="toast" role="status"><Check size={16} /> {toast}<button type="button" onClick={() => setToast(null)} aria-label="Dismiss notification"><X size={14} /></button></div>}
        </div>
      )}
      {toast && !workspaceOpen && <div className="toast" role="status"><Check size={16} /> {toast}<button type="button" onClick={() => setToast(null)} aria-label="Dismiss notification"><X size={14} /></button></div>}
    </div>
  );
}
